import React, { useState } from 'react';
import axios from 'axios';
import { buildApiUrl, toUploadUrl } from '../../config/api';

const LocationForm = ({ location, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: location?.name || '',
        slug: location?.slug || '',
        location: location?.location || '',
        city: location?.city || '',
        state: location?.state || 'Jammu and Kashmir',
        pincode: location?.pincode || '',
        contact_number: location?.contact_number || '',
        description: location?.description || '',
        best_time: location?.best_time || '',
        altitude: location?.altitude || '',
        difficulty: location?.difficulty || 'easy',
        image: location?.image || '',
        status: location?.status || 'active'
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    
    // ✅ Image upload states
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(location?.image ? toUploadUrl(location.image) : '');
    const [uploadProgress, setUploadProgress] = useState(0);

    // ✅ Get token from localStorage
    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Please login to perform this action');
            window.location.href = '/admin/login';
            return null;
        }
        return token;
    };

    // ✅ Handle image selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('Image size should be less than 5MB');
                return;
            }
            
            // Check file type
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file');
                return;
            }
            
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // ✅ Remove image
    const removeImage = () => {
        setImageFile(null);
        setImagePreview('');
        setFormData(prev => ({ ...prev, image: '' }));
    };

    // ✅ Upload image to server
    const uploadImage = async () => {
        if (!imageFile) return null;
        
        const formDataUpload = new FormData();
        formDataUpload.append('image', imageFile);
        formDataUpload.append('folder', 'locations');
        
        try {
            const token = getToken();
            const response = await axios.post(buildApiUrl('/upload'), formDataUpload, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });
            
            if (response.data.success) {
                return response.data.filename;
            }
            return null;
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image');
            return null;
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Auto-generate slug from name
        if (name === 'name' && !location) {
            const slug = value.toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-|-$/g, '');
            setFormData(prev => ({ ...prev, slug }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.slug) newErrors.slug = 'Slug is required';
        if (!formData.location) newErrors.location = 'Location address is required';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setUploadProgress(0);
        
        try {
            const token = getToken();
            if (!token) {
                setLoading(false);
                return;
            }

            // Upload image if new file selected
            let uploadedImage = formData.image;
            if (imageFile) {
                const uploaded = await uploadImage();
                if (uploaded) {
                    uploadedImage = uploaded;
                }
            }
            
            // Prepare data for submission
            const submitData = {
                ...formData,
                image: uploadedImage
            };
            
            // Remove empty values
            Object.keys(submitData).forEach(key => {
                if (submitData[key] === '' || submitData[key] === null) {
                    delete submitData[key];
                }
            });
            
            const url = location 
                ? buildApiUrl(`/camping/admin/locations/${location.id}/update`)
                : buildApiUrl('/camping/admin/locations');

            console.log('📡 URL:', url);
            console.log('📦 Data:', submitData);

            const headers = { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            if (location) {
                await axios.post(url, submitData, { headers });
                alert('✅ Location updated successfully');
            } else {
                await axios.post(url, submitData, { headers });
                alert('✅ Location created successfully');
            }
            
            onSuccess();
        } catch (error) {
            console.error('❌ Error saving location:', error);
            
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else if (error.response?.status === 404) {
                alert(`API endpoint not found: ${error.config?.url}`);
            } else {
                alert(error.response?.data?.message || 'Failed to save location');
            }
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const indianStates = [
        'Jammu and Kashmir', 'Ladakh', 'Himachal Pradesh', 'Uttarakhand',
        'Punjab', 'Haryana', 'Delhi', 'Other'
    ];

    const difficultyLevels = ['easy', 'moderate', 'difficult', 'expert'];

    return (
        <div className="modal-overlay">
            <div className="modal modal-lg">
                <div className="modal-header">
                    <h3>
                        <i className="fas fa-map-marker-alt"></i>
                        {location ? 'Edit Location' : 'Add New Location'}
                    </h3>
                    <button className="close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-tabs">
                        <div className="tab-content">
                            {/* Basic Information */}
                            <div className="form-section">
                                <h4>Basic Information</h4>
                                <div className="form-grid">
                                    <div className="form-group full-width">
                                        <label>Location Name *</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className={errors.name ? 'error' : ''}
                                            placeholder="e.g., Gulmarg Meadow Camp"
                                        />
                                        {errors.name && <span className="error-text">{errors.name}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Slug *</label>
                                        <input
                                            type="text"
                                            name="slug"
                                            value={formData.slug}
                                            onChange={handleChange}
                                            className={errors.slug ? 'error' : ''}
                                            placeholder="gulmarg-meadow-camp"
                                        />
                                        {errors.slug && <span className="error-text">{errors.slug}</span>}
                                    </div>

                                    <div className="form-group full-width">
                                        <label>Location Address *</label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleChange}
                                            className={errors.location ? 'error' : ''}
                                            placeholder="Full address"
                                        />
                                        {errors.location && <span className="error-text">{errors.location}</span>}
                                    </div>
                                </div>
                            </div>

                            {/* Location Details */}
                            <div className="form-section">
                                <h4>Location Details</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>City</label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            placeholder="e.g., Gulmarg"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>State</label>
                                        <select name="state" value={formData.state} onChange={handleChange}>
                                            {indianStates.map(state => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Pincode</label>
                                        <input
                                            type="text"
                                            name="pincode"
                                            value={formData.pincode}
                                            onChange={handleChange}
                                            placeholder="e.g., 193403"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Contact Number</label>
                                        <input
                                            type="text"
                                            name="contact_number"
                                            value={formData.contact_number}
                                            onChange={handleChange}
                                            placeholder="e.g., +91 98765 43210"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Altitude</label>
                                        <input
                                            type="text"
                                            name="altitude"
                                            value={formData.altitude}
                                            onChange={handleChange}
                                            placeholder="e.g., 2,650 m"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Difficulty Level</label>
                                        <select name="difficulty" value={formData.difficulty} onChange={handleChange}>
                                            {difficultyLevels.map(level => (
                                                <option key={level} value={level}>
                                                    {level.charAt(0).toUpperCase() + level.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Best Time to Visit</label>
                                        <input
                                            type="text"
                                            name="best_time"
                                            value={formData.best_time}
                                            onChange={handleChange}
                                            placeholder="e.g., May - October"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="form-section">
                                <h4>Description</h4>
                                <div className="form-group">
                                    <textarea
                                        name="description"
                                        rows="4"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Describe the location, what makes it special, nearby attractions..."
                                    ></textarea>
                                </div>
                            </div>

                            {/* Image Upload */}
                            <div className="form-section">
                                <h4>Location Image</h4>
                                <div className="form-group">
                                    <div className="image-upload-area">
                                        <div className="image-preview">
                                            {imagePreview ? (
                                                <div className="preview-container">
                                                    <img src={imagePreview} alt="Location preview" />
                                                    <button 
                                                        type="button"
                                                        className="remove-image"
                                                        onClick={removeImage}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="upload-placeholder">
                                                    <i className="fas fa-cloud-upload-alt"></i>
                                                    <p>Click or drag to upload location image</p>
                                                    <small>Max size: 5MB. Supported: JPG, PNG, GIF</small>
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="image-input"
                                            id="location-image"
                                        />
                                        <label htmlFor="location-image" className="upload-btn">
                                            <i className="fas fa-upload"></i> {imagePreview ? 'Change Image' : 'Choose Image'}
                                        </label>
                                    </div>
                                    {location?.image && imagePreview && !imageFile && (
                                        <small className="existing-image-note">
                                            Current image: {location.image}
                                        </small>
                                    )}
                                </div>

                                {/* Upload Progress */}
                                {loading && uploadProgress > 0 && (
                                    <div className="upload-progress">
                                        <div className="progress-bar">
                                            <div 
                                                className="progress-fill" 
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                        <span>{uploadProgress}% uploaded</span>
                                    </div>
                                )}
                            </div>

                            {/* Status */}
                            <div className="form-section">
                                <h4>Status</h4>
                                <div className="form-group">
                                    <select name="status" value={formData.status} onChange={handleChange}>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? (uploadProgress > 0 ? `Uploading... ${uploadProgress}%` : 'Saving...') : (location ? 'Update Location' : 'Add Location')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LocationForm;
